import React, { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react';

export type TVDisplayMode = 'pagination' | 'scroll';
export type TVSafeMode = 'default' | 'padding';

export interface TVBoardPage {
  id: string;
  type: 'kanban' | 'other'; // Extensible for future page types
  boardType?: string;
  columnStartIndex?: number;
  columnEndIndex?: number;
  maxColumns?: number;
  hasNextPage?: boolean;
  displayMode?: TVDisplayMode;
}

interface TVBoardContextType {
  // Display mode
  /** Current display mode for TV board */
  displayMode: TVDisplayMode;
  /** Set the display mode */
  setDisplayMode: (mode: TVDisplayMode) => void;
  
  // TV Safe mode for overscan handling
  /** Current TV safe mode for overscan handling */
  safeMode: TVSafeMode;
  /** Set the TV safe mode */
  setSafeMode: (mode: TVSafeMode) => void;
  
  // Current page state
  /** Current page index (0-based) */
  currentPageIndex: number;
  /** Array of all available pages */
  pages: TVBoardPage[];
  /** Currently active page object, null if no pages */
  currentPage: TVBoardPage | null;
  
  // Navigation methods
  /** 
   * Advance to next page. If at end, triggers board completion callback and resets to first page.
   * @param onBoardCompleted - Optional callback to trigger when reaching end of pages
   */
  goToNextPage: (onBoardCompleted?: () => void) => void;
  /** Go to previous page, wraps to last page if at first page */
  goToPreviousPage: () => void;
  /** Set the available pages for current board */
  setPages: (pages: TVBoardPage[]) => void;
  
  // Page completion callback (for triggering board transitions)
  /** Current page completion callback */
  onPageCompleted?: () => void;
  /** Set the page completion callback */
  setOnPageCompleted: (callback: () => void) => void;
  
  // Reset pagination for new board
  /** Reset current page index to 0 */
  resetPagination: () => void;
}

const TVBoardContext = createContext<TVBoardContextType | null>(null);

interface TVBoardProviderProps {
  children: ReactNode;
}

export const TVBoardProvider: React.FC<TVBoardProviderProps> = ({ children }) => {
  const [displayMode, setDisplayMode] = useState<TVDisplayMode>('scroll');
  const [safeMode, setSafeMode] = useState<TVSafeMode>('padding');
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pages, setPages] = useState<TVBoardPage[]>([]);
  const [onPageCompleted, setOnPageCompleted] = useState<(() => void) | undefined>();

  const currentPage = pages[currentPageIndex] || null;

  const goToNextPage = useCallback((onBoardCompleted?: () => void) => {
    const completionCallback = onBoardCompleted || onPageCompleted;
    
    // Handle empty pages case
    if (pages.length === 0) {
      completionCallback?.();
      return;
    }

    // Simple approach: check current state, update, then handle callback
    const isAtLastPage = currentPageIndex >= pages.length - 1;
    
    setCurrentPageIndex(prev => (prev + 1) >= pages.length ? 0 : prev + 1);
    
    // Call completion callback if we were at the last page
    if (isAtLastPage) {
      completionCallback?.();
    }
  }, [pages.length, currentPageIndex, onPageCompleted]); // Removed currentPageIndex from dependencies

  const goToPreviousPage = useCallback(() => {
    setCurrentPageIndex(prevIndex => {
      if (pages.length === 0) return prevIndex;
      return prevIndex > 0 ? prevIndex - 1 : pages.length - 1;
    });
  }, [pages.length]);

  const resetPagination = useCallback(() => {
    setCurrentPageIndex(0);
  }, []);

  const contextValue: TVBoardContextType = useMemo(() => ({
    displayMode,
    setDisplayMode,
    safeMode,
    setSafeMode,
    currentPageIndex,
    pages,
    currentPage,
    goToNextPage,
    goToPreviousPage,
    setPages,
    onPageCompleted,
    setOnPageCompleted,
    resetPagination,
  }), [
    displayMode,
    setDisplayMode,
    safeMode,
    setSafeMode,
    currentPageIndex,
    pages,
    currentPage,
    goToNextPage,
    goToPreviousPage,
    setPages,
    onPageCompleted,
    setOnPageCompleted,
    resetPagination,
  ]);

  return (
    <TVBoardContext.Provider value={contextValue}>
      {children}
    </TVBoardContext.Provider>
  );
};

export const useTVBoard = (): TVBoardContextType => {
  const context = useContext(TVBoardContext);
  if (!context) {
    throw new Error('useTVBoard must be used within a TVBoardProvider');
  }
  return context;
};

/**
 * Creates kanban pages for pagination mode - splits columns into separate pages
 * @param boardType - The type of board (maintenance, repair, etc.)
 * @param totalColumns - Total number of columns to split
 * @param maxColumnsPerPage - Maximum columns per page (default: 4)
 * @returns Array of TVBoardPage objects for pagination
 */
export const createKanbanPages = (
  boardType: string,
  totalColumns: number,
  maxColumnsPerPage: number = 4
): TVBoardPage[] => {
  if (totalColumns <= 0) return [];
  
  const pages: TVBoardPage[] = [];
  
  for (let startIndex = 0; startIndex < totalColumns; startIndex += maxColumnsPerPage) {
    const endIndex = Math.min(startIndex + maxColumnsPerPage - 1, totalColumns - 1);
    const hasNextPage = endIndex < totalColumns - 1;
    
    pages.push({
      id: `${boardType}-kanban-${startIndex}-${endIndex}`,
      type: 'kanban',
      boardType,
      columnStartIndex: startIndex,
      columnEndIndex: endIndex,
      maxColumns: maxColumnsPerPage,
      hasNextPage,
      displayMode: 'pagination',
    });
  }
  
  return pages;
};

/**
 * Creates kanban scroll positions for scroll mode - shows all columns but scrolls through them
 * @param boardType - The type of board (maintenance, repair, etc.)
 * @param totalColumns - Total number of columns
 * @param maxVisibleColumns - Maximum visible columns at once (default: 4)
 * @returns Array of TVBoardPage objects for scrolling
 */
export const createKanbanScrollPositions = (
  boardType: string,
  totalColumns: number,
  maxVisibleColumns: number = 4
): TVBoardPage[] => {
  if (totalColumns <= 0) return [];
  if (totalColumns <= maxVisibleColumns) {
    // If all columns fit, return single position showing all
    return [{
      id: `${boardType}-scroll-0-${totalColumns - 1}`,
      type: 'kanban',
      boardType,
      columnStartIndex: 0,
      columnEndIndex: totalColumns - 1,
      maxColumns: maxVisibleColumns,
      hasNextPage: false,
      displayMode: 'scroll',
    }];
  }
  
  const positions: TVBoardPage[] = [];
  
  // Create scroll positions to ensure we cover all columns including the last one
  // We'll create positions that scroll smoothly and always include the last column
  
  // First position: columns 0 to maxVisibleColumns-1
  positions.push({
    id: `${boardType}-scroll-0-${Math.min(maxVisibleColumns - 1, totalColumns - 1)}`,
    type: 'kanban',
    boardType,
    columnStartIndex: 0,
    columnEndIndex: Math.min(maxVisibleColumns - 1, totalColumns - 1),
    maxColumns: maxVisibleColumns,
    hasNextPage: totalColumns > maxVisibleColumns,
    displayMode: 'scroll',
  });
  
  // Middle positions: scroll by smaller increments to ensure smooth transitions
  if (totalColumns > maxVisibleColumns) {
    const scrollStep = Math.max(1, Math.floor(maxVisibleColumns / 2)); // Scroll by half the visible columns
    
    for (let startIndex = scrollStep; startIndex <= totalColumns - maxVisibleColumns; startIndex += scrollStep) {
      const endIndex = Math.min(startIndex + maxVisibleColumns - 1, totalColumns - 1);
      const hasNextPage = endIndex < totalColumns - 1;
      
      positions.push({
        id: `${boardType}-scroll-${startIndex}-${endIndex}`,
        type: 'kanban',
        boardType,
        columnStartIndex: startIndex,
        columnEndIndex: endIndex,
        maxColumns: maxVisibleColumns,
        hasNextPage,
        displayMode: 'scroll',
      });
    }
    
    // Ensure we have a final position that shows the last column
    const finalStartIndex = totalColumns - maxVisibleColumns;
    const finalEndIndex = totalColumns - 1;
    
    // Only add final position if it's different from the last position we added
    const lastPosition = positions[positions.length - 1];
    if (lastPosition.columnStartIndex !== finalStartIndex) {
      positions.push({
        id: `${boardType}-scroll-${finalStartIndex}-${finalEndIndex}`,
        type: 'kanban',
        boardType,
        columnStartIndex: finalStartIndex,
        columnEndIndex: finalEndIndex,
        maxColumns: maxVisibleColumns,
        hasNextPage: false,
        displayMode: 'scroll',
      });
    }
  }
  
  return positions;
};

/**
 * Creates pages/positions based on display mode
 * @param boardType - The type of board (maintenance, repair, etc.)
 * @param totalColumns - Total number of columns
 * @param maxColumnsPerView - Maximum columns per view (default: 4)
 * @param displayMode - Display mode: 'pagination' or 'scroll' (default: 'scroll')
 * @returns Array of TVBoardPage objects
 */
export const createTVBoardPages = (
  boardType: string,
  totalColumns: number,
  maxColumnsPerView: number = 4,
  displayMode: TVDisplayMode = 'scroll'
): TVBoardPage[] => {
  return displayMode === 'scroll' 
    ? createKanbanScrollPositions(boardType, totalColumns, maxColumnsPerView)
    : createKanbanPages(boardType, totalColumns, maxColumnsPerView);
};